'use client';

import RadioButtonInput from '@/components/form-inputs/RadioButtonInput';
import '@/components/program/travel-customization.styles.css';
import { TextBodyExtraExtraSmall } from '@/components/text/Texts';
import { TRAVEL_PROGRAM_FORM } from '@/constants/forms';
import { TRAVEL_RIDE_TYPE } from '@/constants/travel-program';
import FooterContent from '@/modules/account/components/travel-customization/FooterContent';
import HeaderContent from '@/modules/account/components/travel-customization/HeaderContent';
import type { DialogProps } from 'primereact/dialog';
import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { FaCar } from 'react-icons/fa';
import RideItem from './RideItem';
import SecondaryModal from './SecondaryModal';

interface ModalProps extends DialogProps {
	setVisible: React.Dispatch<React.SetStateAction<boolean>>;
}

const RideTypeModal = (props: ModalProps) => {
	const { setVisible, ...rest } = props;

	const {
		control,
		resetField,
		setValue,
		trigger,
		watch,
		formState: { errors },
	} = useFormContext();

	const rideTypeValue = watch('rideType');

	const handleOnCancel = useCallback(() => {
		resetField('rides', {
			keepError: false,
			keepDirty: false,
			keepTouched: false,
		});
		setValue('rides', []);
		resetField('rideType');
		setVisible(false);
	}, [resetField, setValue, setVisible]);

	const handleOnSave = useCallback(() => {
		trigger(['rides', 'rideType']).then(() => {
			if (rideTypeValue === TRAVEL_RIDE_TYPE.limited && errors.rides) {
				return null;
			}
			setVisible(false);
		});
	}, [errors.rides, rideTypeValue, setVisible, trigger]);

	const handleChange = (value: string) => {
		setValue('rideType', value);
	};

	const handleKeyDown = (e: React.KeyboardEvent, value: string) => {
		if (e.key === 'Enter' || e.key === ' ') {
			handleChange(value);
		}
	};

	return (
		<SecondaryModal
			{...rest}
			draggable={false}
			header={
				<HeaderContent
					label="Ride Type"
					icon={<FaCar size={30} className="text-primary" />}
				/>
			}
			footer={
				<FooterContent
					formName={TRAVEL_PROGRAM_FORM}
					id="ride-type"
					onClickCancel={handleOnCancel}
					onClickSave={handleOnSave}
				/>
			}
			style={{ width: '45rem' }}
		>
			<div className="font-poppins">
				<div className="mb-6">
					<p className="text-base">
						Do you want to limit the type of trips people can
						charge?
					</p>
				</div>

				<div>
					<div
						className={
							'flex items-center justify-between py-3 border-b border-slate-300 cursor-pointer'
						}
						onClick={() => handleChange(TRAVEL_RIDE_TYPE.all)}
						onKeyDown={(e) =>
							handleKeyDown(e, TRAVEL_RIDE_TYPE.all)
						}
					>
						<RadioButtonInput
							control={control}
							formName={TRAVEL_PROGRAM_FORM}
							id="rideType"
							label="All Ride Types"
							value={TRAVEL_RIDE_TYPE.all}
						/>
					</div>

					<div
						className={
							'flex items-center justify-between py-3 border-b border-slate-300 cursor-pointer'
						}
						onClick={() => handleChange(TRAVEL_RIDE_TYPE.limited)}
						onKeyDown={(e) =>
							handleKeyDown(e, TRAVEL_RIDE_TYPE.limited)
						}
					>
						<RadioButtonInput
							control={control}
							formName={TRAVEL_PROGRAM_FORM}
							id="rideType"
							label="Limited Ride Types"
							value={TRAVEL_RIDE_TYPE.limited}
						/>
					</div>
					{errors.rides ? (
						<TextBodyExtraExtraSmall
							id={'rides-helper'}
							className="block pl-[16px] mt-1 text-error"
							text="Please select at least one (1) ride type."
						/>
					) : (
						''
					)}

					{rideTypeValue === TRAVEL_RIDE_TYPE.limited && <RideItem />}
				</div>
			</div>
		</SecondaryModal>
	);
};

export default RideTypeModal;
